import React, { useState, useEffect, useRef } from 'react';
import { useApp } from '../../context/AppContext';
import { getTaux } from '../../data/tarifsData';
import './Services.css';

const DEVISES = [
  { key: 'fcfa', label: 'FCFA' },
  { key: 'usd',  label: 'USD'  },
  { key: 'eur',  label: 'EUR'  },
];

// ── Formatage d'un item de tarif indicatif ────────────────
// Deux cas gérés :
//  1. item.fcfa est un NOMBRE  → montant réel, convertible dans les 3 devises.
//  2. item.fcfa est null/absent → on affiche item.prix TEL QUEL, sans
//     conversion (ex : "Sur devis", "5–8%", des taux qui ne sont pas
//     des montants FCFA convertibles).
function fmtItem(item, devise, TAUX) {
  if (item.fcfa == null) return item.prix || 'Sur devis';
  if (devise === 'usd') return Math.round(item.fcfa / TAUX.USD).toLocaleString('fr-FR') + ' $' + (item.unit ? `/${item.unit}` : '');
  if (devise === 'eur') return Math.round(item.fcfa / TAUX.EUR).toLocaleString('fr-FR') + ' €' + (item.unit ? `/${item.unit}` : '');
  return Math.round(item.fcfa).toLocaleString('fr-FR') + ' FCFA' + (item.unit ? `/${item.unit}` : '');
}

function fmtDomainePrice(d, devise, TAUX) {
  if (d.priceFcfa == null) return d.price || 'Sur devis';
  if (devise === 'usd') return Math.round(d.priceFcfa / TAUX.USD).toLocaleString('fr-FR') + ' $';
  if (devise === 'eur') return Math.round(d.priceFcfa / TAUX.EUR).toLocaleString('fr-FR') + ' €';
  return Math.round(d.priceFcfa).toLocaleString('fr-FR') + ' FCFA';
}

// ── Carousel photos pour un domaine ──────────────────────
function DomainGallery({ images }) {
  const [idx, setIdx] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!images || images.length <= 1) return;
    timerRef.current = setInterval(() => setIdx(i => (i + 1) % images.length), 3500);
    return () => clearInterval(timerRef.current);
  }, [images]);

  if (!images || images.length === 0) return null;

  const prev = () => {
    clearInterval(timerRef.current);
    setIdx(i => (i - 1 + images.length) % images.length);
  };
  const next = () => {
    clearInterval(timerRef.current);
    setIdx(i => (i + 1) % images.length);
  };

  return (
    <div className="dg-wrap">
      <div className="dg-slide">
        <img src={images[idx].data || images[idx]} alt={images[idx].caption || ''} className="dg-img" />
        {images[idx].caption && <div className="dg-caption">{images[idx].caption}</div>}
        {images.length > 1 && (
          <>
            <button className="dg-arrow dg-prev" onClick={prev}>‹</button>
            <button className="dg-arrow dg-next" onClick={next}>›</button>
          </>
        )}
      </div>
      {images.length > 1 && (
        <div className="dg-dots">
          {images.map((_, i) => (
            <span key={i} className={'dg-dot' + (i === idx ? ' active' : '')} onClick={() => setIdx(i)} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main Services section ─────────────────────────────────
export default function Services() {
  const { data } = useApp();
  const [devise, setDevise] = useState('fcfa');
  const TAUX = getTaux(data.tauxChange || {});

  return (
    <section className="ms bg2" id="services">
      <div className="sh">
        <div className="stag">Nos Domaines</div>
        <h2 className="stitle">Expertise Polyvalente &amp;<br />Solutions Sur Mesure</h2>
        <p className="sdesc">Du numérique à l'énergie, de l'immobilier au forage — solutions complètes pour particuliers et entreprises.</p>

        {/* Sélecteur devise */}
        <div className="dig-devise-btns" style={{ marginTop: '18px' }}>
          {DEVISES.map(d => (
            <button
              key={d.key}
              className={`dig-devise-btn ${devise === d.key ? 'active' : ''}`}
              onClick={() => setDevise(d.key)}
            >
              {d.label}
            </button>
          ))}
        </div>
      </div>
      <div className="dgrid">
        {data.domaines.map((d, i) => (
          <div key={d.id} className="dc" style={{ animationDelay: `${i * 0.05}s` }}>
            <div className="dc-top">
              <div className="dc-icon">{d.icon}</div>
              <div>
                <div className="dc-name">{d.name}</div>
                <div className="dc-desc">{d.desc}</div>
              </div>
            </div>

            {/* ── Galerie photos ── */}
            {d.images?.length > 0 && <DomainGallery images={d.images} />}

            <div className="dc-body">
              <ul className="dc-svcs">
                {d.services.map((s, j) => <li key={j}>{s}</li>)}
              </ul>
            </div>

            {d.ti?.length > 0 && (
              <div className="dc-ti">
                <div className="ti-lbl">📋 Tarif Indicatif (TI)</div>
                <div className="ti-grid">
                  {d.ti.map((t, j) => (
                    <div key={j} className="ti-item">
                      <div className="ti-svc">{t.svc}</div>
                      <div className="ti-prix">{fmtItem(t, devise, TAUX)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="dc-foot">
              <div>
                <div className="price-lbl">{d.priceLabel}</div>
                <div className="price-val">{fmtDomainePrice(d, devise, TAUX)}</div>
              </div>
              <a href="#contact" className="bsm bsm-blue">Demander →</a>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}